package com.evoapps.evoshift.domain.usecase.team

import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.capabilities
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository

/**
 * SH-192: someone who actively works at another workplace the caller manages, offered when the
 * caller adds a person to a workplace (Team → Add employee → From your other workplaces).
 */
data class OtherWorkplacePerson(
    val user: User,
    /** Where they work for the caller (the first such workplace when there are several). */
    val workplaceId: String,
    val workplaceName: String,
    /** Their role there (null: none set). */
    val role: String?,
    /**
     * Whether adding them takes a new seat of the target workplace's owner's plan: the plan counts
     * each person once per owner (functions/planAccess.js seatHolders), so someone already
     * actively working for the same owner, or the owner themselves, takes none. True when the
     * target's owner is not known (the seat check then decides as for a new person).
     */
    val takesNewSeat: Boolean
)

/**
 * SH-192: the people of the caller's other workplaces they may add to [workplaceId]: those with
 * an active engagement at a workplace where the caller manages the team (firestore.rules:
 * mayAddEngagementTo, isManagerOfUser), minus anyone with an engagement of any state at
 * [workplaceId] already, the caller, and anyone at the rules' limit of [MAX_ENGAGEMENTS]
 * workplaces. Sorted by name.
 */
class GetPeopleFromOtherWorkplacesUseCase(
    private val userRepository: UserRepository,
    private val workplaceRepository: WorkplaceRepository
) {
    suspend operator fun invoke(me: User, workplaceId: String): Result<List<OtherWorkplacePerson>> {
        val managed = me.engagements.values.filter {
            it.workplaceId != workplaceId && Capability.MANAGE_TEAM in it.capabilities
        }
        if (managed.isEmpty()) return Result.success(emptyList())

        val teams = managed.map { engagement -> engagement to userRepository.getAssignableEmployees(engagement.workplaceId) }
        // Nothing could be read: say so (one unreadable team still shows the others).
        if (teams.all { it.second.isFailure }) {
            return Result.failure(teams.first().second.exceptionOrNull() ?: IllegalStateException("teams of other workplaces"))
        }

        val ownerOf: Map<String, String?> = (managed.map { it.workplaceId } + workplaceId)
            .associateWith { workplaceRepository.getWorkplace(it).getOrNull()?.ownerId }
        val targetOwner = ownerOf[workplaceId]

        fun takesNewSeat(user: User): Boolean {
            if (targetOwner == null) return true
            if (user.uid == targetOwner) return false
            return user.engagements.values.none { it.isActive && ownerOf[it.workplaceId] == targetOwner }
        }

        val people = linkedMapOf<String, OtherWorkplacePerson>()
        teams.forEach { (engagement, team) ->
            team.getOrNull().orEmpty().forEach { user ->
                val there = user.engagements[engagement.workplaceId]
                val eligible = user.uid != me.uid && workplaceId !in user.engagements &&
                    user.engagements.size < MAX_ENGAGEMENTS && there?.isActive == true
                if (eligible && user.uid !in people) {
                    people[user.uid] = OtherWorkplacePerson(
                        user = user,
                        workplaceId = engagement.workplaceId,
                        workplaceName = engagement.cachedWorkplaceName ?: there.cachedWorkplaceName.orEmpty(),
                        role = there.role?.takeIf { it.isNotBlank() },
                        takesNewSeat = takesNewSeat(user)
                    )
                }
            }
        }
        return Result.success(people.values.sortedBy { it.user.displayName.lowercase() })
    }

    companion object {
        /** firestore.rules check up to 6 engagements per person (unrolled; 1000-expression limit). */
        const val MAX_ENGAGEMENTS = 6
    }
}
